/* eslint-disable react-hooks/refs */
import { useEffect, useRef, useCallback } from 'react';

import { config, logger } from '@tankef/env';

// ============================================================================
// TYPES
// ============================================================================

export interface Channel {
  name: string;
  params?: Record<string, string>;
}

export type MessageHandler<T = unknown> = (data: T) => void;

interface ActionCableMessage {
  identifier?: string;
  message?: unknown;
  type?: 'ping' | 'welcome' | 'confirm_subscription' | 'reject_subscription';
}

interface ActionCableCommand {
  command: 'subscribe' | 'unsubscribe' | 'message';
  identifier: string;
  data?: string;
}

// ============================================================================
// WEBSOCKET CLIENT
// ============================================================================

class WebSocketClient {
  private ws: WebSocket | null = null;
  private subscriptions = new Map<string, Set<MessageHandler>>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private token: string | null = null;
  private tokenGetter: (() => string | null) | null = null;
  private welcomeReceived = false;
  private isConnecting = false;
  private connectionPromise: Promise<void> | null = null;
  private manualDisconnect = false;
  private connectionConsumers = 0;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Called when the server closes before welcome (expired token). Caller should refresh the token. */
  onAuthRejected: (() => Promise<void>) | null = null;

  acquire(getToken: () => string | null): Promise<void> {
    this.tokenGetter = getToken;
    const token = getToken();
    if (!token) return Promise.reject(new Error('No token available'));
    this.connectionConsumers++;
    this.clearDisconnectTimer();
    return this.connect(token);
  }

  release(): void {
    this.connectionConsumers = Math.max(0, this.connectionConsumers - 1);

    if (this.connectionConsumers > 0) {
      return;
    }

    this.clearDisconnectTimer();
    this.disconnectTimer = setTimeout(() => {
      if (this.connectionConsumers === 0) {
        this.disconnect();
      }
    }, 250);
  }

  /**
   * Connect to WebSocket server
   */
  connect(token: string): Promise<void> {
    this.clearDisconnectTimer();
    this.clearReconnectTimer();

    if (this.isConnected() && this.token === token) {
      return Promise.resolve();
    }

    if (this.isConnecting && this.connectionPromise && this.token === token) {
      return this.connectionPromise;
    }

    if (this.ws && this.token !== token) {
      this.disconnect();
    }

    this.token = token;
    this.isConnecting = true;
    this.manualDisconnect = false;
    this.welcomeReceived = false;

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // SECURITY: token rides on Sec-WebSocket-Protocol. The legacy
        // ?token= query-string fallback was removed once BE confirmed
        // subprotocol acceptance across all environments (production
        // rejects ?token= unconditionally; see BE
        // app/channels/application_cable/connection.rb#query_string_enabled?).
        // Tokens in URLs leak via reverse-proxy access logs, traces,
        // History API, and Referrer headers (OWASP A02 + A09).
        logger.debug('WebSocket connecting...', { url: config.wsUrl, transport: 'subprotocol' });

        // FE MUST send TWO subprotocols: a public one the server can echo back
        // (`actioncable-v1-json`) AND the bearer token. ActionCable's
        // ConnectionManager refuses to echo the `bearer.*` subprotocol so the
        // token never leaks into the response Sec-WebSocket-Protocol header
        // (and from there into proxy logs / traces). If only the bearer is
        // sent, the server has nothing safe to echo and the browser aborts
        // the handshake with "Sent non-empty 'Sec-WebSocket-Protocol' header
        // but no response was received". See BE connection.rb#from_subprotocol_header.
        this.ws = new WebSocket(config.wsUrl, ['actioncable-v1-json', `bearer.${token}`]);

        this.ws.onopen = () => {
          logger.info('WebSocket connected');
          this.reconnectAttempts = 0;
          this.isConnecting = false;
          this.connectionPromise = null;
          this.startPing();
          this.resubscribeAll();
          resolve();
        };

        this.ws.onmessage = event => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = event => {
          this.isConnecting = false;
          this.connectionPromise = null;
          this.stopPing();

          if (this.manualDisconnect || this.connectionConsumers === 0) {
            logger.debug('WebSocket closed intentionally', {
              code: event.code,
              reason: event.reason || '(user-initiated)',
            });
            return;
          }

          // Interpret close code
          const closeReason = this.interpretCloseCode(event.code, event.reason);
          logger.warn('WebSocket closed unexpectedly', {
            code: event.code,
            reason: event.reason || closeReason,
          });

          // Only reconnect on certain close codes
          if (this.shouldReconnect(event.code)) {
            this.attemptReconnect();
          } else if (event.code === 1000 && !this.welcomeReceived) {
            // ActionCable closed with 1000 before welcome — token likely expired.
            // Trigger caller-provided refresh (if any), then retry once after 3s.
            logger.warn('WebSocket auth rejection — triggering token refresh then retrying once');
            const doRefresh = this.onAuthRejected ? this.onAuthRejected() : Promise.resolve();
            void doRefresh.finally(() => {
              setTimeout(() => {
                const freshToken = this.tokenGetter?.();
                if (freshToken && freshToken !== this.token && this.connectionConsumers > 0) {
                  logger.info('Token refreshed, reconnecting WebSocket');
                  this.token = null; // force connect() to proceed
                  this.reconnectAttempts = 0;
                  void this.connect(freshToken).catch(err => {
                    logger.error('WebSocket post-refresh reconnect failed', err);
                  });
                } else {
                  logger.error('WebSocket closed with unrecoverable error', { code: event.code });
                  this.disconnect();
                }
              }, 3000);
            });
          } else {
            logger.error('WebSocket closed with unrecoverable error', { code: event.code });
            this.disconnect();
          }
        };

        this.ws.onerror = error => {
          if (this.manualDisconnect || this.connectionConsumers === 0) {
            return;
          }

          logger.error('WebSocket error', error);
          this.isConnecting = false;
          this.connectionPromise = null;
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    logger.info('WebSocket disconnecting...');
    this.clearDisconnectTimer();
    this.clearReconnectTimer();
    this.stopPing();
    this.manualDisconnect = true;
    this.isConnecting = false;
    this.connectionPromise = null;

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close(1000, 'client disconnect');
      this.ws = null;
    }

    this.token = null;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }

  /**
   * Subscribe to a channel
   */
  subscribe<T = unknown>(channel: Channel, handler: MessageHandler<T>): () => void {
    const identifier = this.getIdentifier(channel);

    if (!this.subscriptions.has(identifier)) {
      this.subscriptions.set(identifier, new Set());
      if (this.isConnected()) {
        this.sendCommand('subscribe', identifier);
      }
    }

    this.subscriptions.get(identifier)!.add(handler as MessageHandler);
    logger.debug('Subscribed to channel', { channel: channel.name });

    // Return unsubscribe function
    return () => {
      const handlers = this.subscriptions.get(identifier);
      if (handlers) {
        handlers.delete(handler as MessageHandler);
        if (handlers.size === 0) {
          this.subscriptions.delete(identifier);
          if (this.isConnected()) {
            this.sendCommand('unsubscribe', identifier);
          }
          logger.debug('Unsubscribed from channel', { channel: channel.name });
        }
      }
    };
  }

  /**
   * Send a message to a channel
   */
  perform<T = unknown>(channel: Channel, action: string, data?: T): void {
    const identifier = this.getIdentifier(channel);
    this.sendCommand('message', identifier, JSON.stringify({ action, ...data }));
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // --- Private methods ---

  private getIdentifier(channel: Channel): string {
    return JSON.stringify({ channel: channel.name, ...channel.params });
  }

  private handleMessage(data: string): void {
    try {
      const message: ActionCableMessage = JSON.parse(data);

      // Handle ActionCable protocol messages
      if (message.type === 'ping') {
        return; // Ignore pings
      }

      if (message.type === 'welcome') {
        logger.debug('WebSocket welcome received');
        this.welcomeReceived = true;
        return;
      }

      if (message.type === 'confirm_subscription') {
        logger.debug('Subscription confirmed', { identifier: message.identifier });
        return;
      }

      if (message.type === 'reject_subscription') {
        logger.warn('Subscription rejected', { identifier: message.identifier });
        return;
      }

      // Handle channel messages
      if (message.identifier && message.message !== undefined) {
        const handlers = this.subscriptions.get(message.identifier);
        if (handlers) {
          handlers.forEach(handler => {
            try {
              handler(message.message);
            } catch (error) {
              logger.error('Error in message handler', error);
            }
          });
        }
      }
    } catch (error) {
      logger.error('Error parsing WebSocket message', error);
    }
  }

  private sendCommand(
    command: ActionCableCommand['command'],
    identifier: string,
    data?: string
  ): void {
    if (!this.isConnected()) {
      logger.warn('WebSocket not connected, cannot send command');
      return;
    }

    const message: ActionCableCommand = { command, identifier };
    if (data) {
      message.data = data;
    }

    this.ws!.send(JSON.stringify(message));
  }

  private resubscribeAll(): void {
    this.subscriptions.forEach((_, identifier) => {
      this.sendCommand('subscribe', identifier);
    });
  }

  private attemptReconnect(): void {
    this.clearReconnectTimer();

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached');
      return;
    }

    if (this.connectionConsumers === 0) {
      logger.debug('Skipping reconnect without active consumers');
      return;
    }

    // Always read the latest token — the HTTP layer may have refreshed it since
    // the last connection attempt (stored in localStorage via TokenManager).
    const freshToken = this.tokenGetter?.() ?? this.token;
    if (!freshToken) {
      logger.warn('No token available for reconnection');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    logger.info(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      // Re-read token at fire time — refresh may complete during the delay
      const tokenAtFire = this.tokenGetter?.() ?? freshToken;
      void this.connect(tokenAtFire).catch(error => {
        logger.warn('WebSocket reconnect attempt failed', error);
      });
    }, delay);
  }

  private startPing(): void {
    // Send keepalive ping every 30 seconds to keep connection alive
    // ActionCable expects pings to prevent proxy timeouts
    this.pingInterval = setInterval(() => {
      if (this.isConnected() && this.ws) {
        try {
          // Send ActionCable ping message to server
          this.ws!.send(JSON.stringify({ type: 'ping' }));
          logger.debug('WebSocket keepalive ping sent');
        } catch (error) {
          logger.warn('Failed to send keepalive ping', error);
        }
      }
    }, 25000); // 25 seconds, before typical 30-60 second proxy timeout
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Interpret WebSocket close code and return human-readable reason
   */
  private interpretCloseCode(code: number, reason: string): string {
    if (reason) return reason;

    const codeMap: Record<number, string> = {
      1000: 'Normal closure (no reason provided - may indicate server-side disconnect)',
      1001: 'Going away',
      1002: 'Protocol error',
      1003: 'Unsupported data',
      1004: 'Reserved',
      1005: 'No status received',
      1006: 'Abnormal closure (connection lost)',
      1007: 'Invalid frame payload',
      1008: 'Policy violation',
      1009: 'Message too big',
      1010: 'Mandatory extension',
      1011: 'Server error',
      1012: 'Service restart',
      1013: 'Try again later',
      1014: 'Bad gateway',
      1015: 'TLS handshake failure',
      4000: 'Server rejected connection (likely JWT/auth issue)',
      4001: 'Token expired or invalid',
      4002: 'User inactive',
      4003: 'Session invalid',
      4004: 'User not found',
    };

    return codeMap[code] || `Unknown close code: ${code}`;
  }

  /**
   * Determine if we should attempt to reconnect based on close code.
   *
   * ActionCable rejects expired/invalid tokens by closing with code 1000
   * (graceful) before ever sending the `welcome` frame. We detect this
   * by checking `welcomeReceived`: if the connection closed normally but
   * ActionCable never greeted us, it was an auth rejection — don't retry
   * with the same dead token; the HTTP layer will refresh it and the React
   * layer will reconnect once the token changes.
   */
  private shouldReconnect(code: number): boolean {
    // Don't reconnect on explicit auth/validation errors (4xxx codes)
    if (code >= 4000 && code < 5000) {
      logger.error('WebSocket rejected due to authentication/validation error - not reconnecting');
      return false;
    }

    // Code 1000 without a prior welcome = ActionCable rejected the token.
    // Retrying immediately would hammer the server with the same expired token.
    if (code === 1000 && !this.welcomeReceived) {
      logger.error('WebSocket closed before welcome — token likely expired, not reconnecting');
      return false;
    }

    // Don't reconnect on policy/protocol violations
    if (code >= 1000 && code <= 1011 && ![1000, 1006].includes(code)) {
      return false;
    }

    // Reconnect on:
    // 1000 - Normal closure after a live session (server restart, etc.)
    // 1006 - Abnormal closure (connection lost)
    return true;
  }

  private clearDisconnectTimer(): void {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// Singleton instance
export const wsClient = new WebSocketClient();

// ============================================================================
// PREDEFINED CHANNELS
// ============================================================================

// Each Channel here must match a backend channel under
// tankef-pay-service/app/channels/. Adding a Channel without backend
// support causes subscriptions to be silently rejected.
export const BalanceChannel: Channel = { name: 'BalanceChannel' };
export const NotificationsChannel: Channel = { name: 'NotificationsChannel' };
export const KycStatusChannel: Channel = { name: 'KycStatusChannel' };
export const PaymentStatusChannel: Channel = { name: 'PaymentStatusChannel' };
export const BatchStatusChannel: Channel = { name: 'BatchStatusChannel' };
export const OnboardingChannel: Channel = { name: 'OnboardingChannel' };
export const RelayChannel: Channel = { name: 'RelayChannel' };
export const SystemAlertsChannel: Channel = { name: 'SystemAlertsChannel' };
export const AccountChannel: Channel = { name: 'AccountChannel' };

// ============================================================================
// CHANNEL MESSAGE TYPES
// ============================================================================

export interface BalanceUpdate {
  type:
    | 'deposit_received'
    | 'balance_updated'
    | 'withdrawal_completed'
    | 'transfer_debited'
    | 'account_updated'
    | 'hold_created'
    | 'hold_released'
    | 'hold_settled';
  timestamp: string;
  payload: {
    account_id: string;
    available_cents: number;
    held_cents: number;
    ledger_cents?: number;
    currency?: string;
    amount_cents?: number;
    hold_id?: string;
    hold_type?: string;
    reference?: string;
    sender_name?: string;
    beneficiary_name?: string;
    reason?: string;
    change_cents?: number;
    change_type?: string;
  };
}

export interface KycStatusUpdate {
  type:
    | 'kyc_started'
    | 'kyc_document_uploaded'
    | 'kyc_document_verified'
    | 'kyc_document_rejected'
    | 'kyc_verification_progress'
    | 'kyc_approved'
    | 'kyc_rejected'
    | 'kyc_level_upgraded'
    | 'kyc_pending_review'
    | 'kyc_documents_required';
  timestamp: string;
  payload: {
    party_id: string;
    kyc_level?: string;
    old_level?: string;
    new_level?: string;
    new_limits?: {
      daily_cents: number;
      monthly_cents: number;
      per_transaction_cents: number;
    };
    rejection_reason?: string;
    can_retry?: boolean;
    check_type?: string;
    document_type?: string;
    step?: number;
    total_steps?: number;
    percentage?: number;
    message?: string;
  };
}

// PaymentStatusChannel events MUST match what the backend emits in
// app/channels/payment_status_channel.rb (#status_event_type + transmit_current_status):
//
//   payment_created     — broadcast when PaymentOrder is created
//   payment_processing  — broadcast when status transitions to processing
//   payment_completed   — broadcast on terminal success
//   payment_failed      — broadcast on terminal failure
//   payment_progress    — periodic progress signal
//   payment_error       — non-terminal error event
//   current_status      — initial transmit on subscribe (subscribe-time snapshot)
//
// The previous union (`payment_processed | payment_failed | payment_pending |
// payment_reversed`) DID NOT MATCH backend reality — toasts and cache
// invalidations were silently misfiring. Aligned here.
export interface PaymentStatusUpdate {
  type:
    | 'payment_created'
    | 'payment_processing'
    | 'payment_completed'
    | 'payment_failed'
    | 'payment_progress'
    | 'payment_error'
    | 'current_status';
  payment_id: string;
  order_id?: string;
  amount_cents: number;
  status: string;
  failure_reason?: string;
}

/** OnboardingChannel uses a flat structure (no nested payload). */
export interface OnboardingUpdate {
  type:
    | 'initial_status'
    | 'status_changed'
    | 'kyc_update'
    | 'compliance_update'
    | 'status_update'
    | 'esign_update'; // tankef-pay-service PR #360 — reemplaza polling de esign-status
  state?: string;
  previous_state?: string;
  current_step?: string;
  progress?: number;
  flow_version?: string;
  kyc_status?: string;
  compliance_status?: string;
  risk_level?: string;
  // Campos específicos de esign_update:
  contract_id?: string;
  esign_status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'expired';
  provider?: string; // "jaak"
  signature_request_id?: string;
  signed_at?: string;
  failure_reason?: string;
  timestamp?: string;

  // ── Shape del OnboardingChannelRelay (outbox events `onboarding.*`) ─────────
  // El relay (tankef-pay-service, audit 2026-06-09) broadcastea
  // `{ topic, payload, broadcasted_at }` — distinto del shape plano de
  // esign_update/status_changed de arriba. Eventos: clabe_ready,
  // legal_review_completed, kyb_failed, document_extracted, etc. Verificado
  // contra develop (PR #421). Ver docs/plans/junio2026-be-fe-gaps-audit.md.
  topic?: string;
  payload?: Record<string, unknown>;
  broadcasted_at?: string;
}

/**
 * RelayChannel — todos los eventos de una tanda específica (suscripción por
 * `relay_id`). El BE wrapea cada evento en `{ event, payload, timestamp }`
 * vía `RelayChannel.broadcast`. Mapeo de payloads en
 * `tankef-pay-service/domains/tandas/app/application/subscribers/websocket_broadcast_subscriber.rb`.
 */
export type RelayEventType =
  | 'participant_joined'
  | 'participant_removed'
  | 'participants_reordered'
  | 'relay_activated'
  | 'relay_completed'
  | 'relay_cancelled'
  | 'relay_updated'
  | 'payment_updated'
  | 'payment_received'
  | 'payment_requested'
  | 'distribution_requested'
  | 'invitation_sent'
  | 'invitation_accepted'
  | 'invitation_revoked'
  | 'turn_requested'
  | 'turn_request_approved'
  | 'turn_request_rejected'
  | 'tanda_recalculated';

export interface RelayUpdate {
  event: RelayEventType;
  payload: {
    relay_id: string;
    [key: string]: unknown;
  };
  timestamp: string;
}

/**
 * NotificationsChannel — eventos por usuario (stream_for current_user).
 * Match con `app/channels/notifications_channel.rb`.
 */
export interface NotificationUpdate {
  type:
    | 'new_notification'
    | 'notification_read'
    | 'unread_count_updated'
    | 'batch_notifications'
    | 'recent_notifications'
    | 'pending_checks'
    | 'error';
  payload?: {
    notification?: {
      id: string;
      notification_type: string;
      title: string;
      body: string;
      category: string;
      priority: string;
      mandatory: boolean;
      channel: string;
      status: string;
      read_at: string | null;
      created_at: string;
      action_url?: string;
      context_data?: Record<string, unknown>;
    };
    notification_id?: string;
    notifications?: unknown[];
    count?: number;
    unread_count?: number;
  };
  timestamp?: string;
  message?: string;
}

/**
 * SystemAlertsChannel — alertas tenant-wide (stream_for current_tenant).
 * Match con `app/channels/system_alerts_channel.rb`. El payload incluye
 * `alert_type` (e.g. "spei_outage") + severity + título + mensaje.
 */
export interface SystemAlertUpdate {
  type: 'system_alert';
  payload: {
    alert_type: string;
    severity: 'info' | 'warning' | 'critical';
    title: string;
    message: string;
    user_status_key?: string;
  };
  timestamp: string;
}

/**
 * AccountChannel — eventos multi-user PM (suscripción por `account_id`).
 * Match con `app/channels/account_channel.rb` + `account_channel_relay.rb`.
 *
 * IMPORTANTE: este canal usa `topic` (no `type`) y `broadcasted_at` (no
 * `timestamp`) — el shape lo determina `AccountChannelRelay` que pela el
 * Outbox event y lo pasa tal cual al `broadcast_to`. Mantener consistente
 * cuando se implemente el handler.
 *
 * Eventos: account.user_invited, account.user_accepted, account.role_changed,
 * account.user_removed, account.invitation_revoked.
 */
export type AccountTopic =
  | 'account.user_invited'
  | 'account.user_accepted'
  | 'account.role_changed'
  | 'account.user_removed'
  | 'account.invitation_revoked';

export interface AccountUpdate {
  topic: AccountTopic;
  payload: {
    account_id: string;
    tenant_id: string;
    role?: string;
    user_id?: string;
    invitation_id?: string;
    [key: string]: unknown;
  };
  broadcasted_at: string;
}

// ============================================================================
// REACT HOOKS
// ============================================================================

/**
 * Hook to subscribe to a WebSocket channel
 */
export function useChannel<T = unknown>(
  channel: Channel,
  handler: MessageHandler<T>,
  enabled = true
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const channelName = channel.name;
  const channelParamsKey = JSON.stringify(channel.params);

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = wsClient.subscribe<T>(channel, data => {
      handlerRef.current(data);
    });

    return unsubscribe;
    // channel is re-derived from channelName + channelParamsKey; skipping it avoids resubscribe on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, channelParamsKey, enabled]);
}

/**
 * Hook for balance updates
 */
export function useBalanceUpdates(
  accountId: string | undefined,
  handler: (update: BalanceUpdate) => void,
  enabled = true
): void {
  const channel = useRef<Channel>(BalanceChannel);
  channel.current = accountId
    ? { ...BalanceChannel, params: { account_id: accountId } }
    : BalanceChannel;

  useChannel(channel.current, handler, enabled && !!accountId);
}

/**
 * Hook for KYC status changes (approved, rejected, level upgraded).
 * Scoped to a specific party_id when provided.
 */
export function useKycStatusUpdates(
  partyId: string | undefined,
  handler: (update: KycStatusUpdate) => void,
  enabled = true
): void {
  const channel = useRef<Channel>(KycStatusChannel);
  channel.current = partyId
    ? { ...KycStatusChannel, params: { party_id: partyId } }
    : KycStatusChannel;

  useChannel(channel.current, handler, enabled && !!partyId);
}

/**
 * Hook for payment status updates. Receives every PaymentStatusUpdate event
 * type emitted by PaymentStatusChannel: created, processing, completed, failed,
 * progress, error, and the initial current_status snapshot on subscribe.
 */
export function usePaymentStatusUpdates(
  handler: (update: PaymentStatusUpdate) => void,
  enabled = true
): void {
  useChannel(PaymentStatusChannel, handler, enabled);
}

/**
 * Hook for onboarding session updates (status changes, KYC progress, compliance).
 * Scoped to a specific session_id — backend streams from `onboarding_<session_id>`.
 */
export function useOnboardingUpdates(
  sessionId: string | undefined,
  handler: (update: OnboardingUpdate) => void,
  enabled = true
): void {
  const channel = useRef<Channel>(OnboardingChannel);
  channel.current = sessionId
    ? { ...OnboardingChannel, params: { session_id: sessionId } }
    : OnboardingChannel;

  useChannel(channel.current, handler, enabled && !!sessionId);
}

/**
 * Hook para eventos en tiempo real de una tanda (RelayChannel).
 * Suscripción scoped por `relay_id` — el BE valida que el current_user
 * sea organizador o miembro activo.
 *
 * Eventos típicos (18 total): participant_joined, relay_activated,
 * payment_updated, invitation_sent, turn_requested, tanda_recalculated, etc.
 * Ver el mapeo completo en `RelayEventType`.
 */
export function useRelayUpdates(
  relayId: string | undefined,
  handler: (update: RelayUpdate) => void,
  enabled = true
): void {
  const channel = useRef<Channel>(RelayChannel);
  channel.current = relayId ? { ...RelayChannel, params: { relay_id: relayId } } : RelayChannel;

  useChannel(channel.current, handler, enabled && !!relayId);
}

/**
 * Hook para notificaciones del usuario (NotificationsChannel).
 * Suscripción scoped automáticamente por el current_user del JWT.
 *
 * Eventos: new_notification, notification_read, unread_count_updated,
 * batch_notifications, recent_notifications (snapshot inicial).
 */
export function useNotificationUpdates(
  handler: (update: NotificationUpdate) => void,
  enabled = true
): void {
  useChannel(NotificationsChannel, handler, enabled);
}

/**
 * Hook para alertas de sistema tenant-wide (SystemAlertsChannel).
 * Suscripción scoped automáticamente por el current_tenant del JWT.
 *
 * Eventos: system_alert (con payload.alert_type — spei_outage,
 * circuit_breaker_opened, etc.). El handler típico muestra un banner
 * persistente o toast crítico.
 */
export function useSystemAlerts(
  handler: (update: SystemAlertUpdate) => void,
  enabled = true
): void {
  useChannel(SystemAlertsChannel, handler, enabled);
}

/**
 * Hook para eventos multi-user PM (AccountChannel). Suscripción scoped
 * por `account_id` — el BE valida que el current_user sea AccountUser
 * activo del account.
 *
 * 5 eventos: account.user_invited, account.user_accepted,
 * account.role_changed, account.user_removed, account.invitation_revoked.
 *
 * Pendiente de integración en RealtimeGateProvider cuando aterrice el
 * PR 11 PM (Multi-user dashboard). Mientras tanto, este hook permite
 * que los componentes interesados se suscriban directamente.
 */
export function useAccountUpdates(
  accountId: string | undefined,
  handler: (update: AccountUpdate) => void,
  enabled = true
): void {
  const channel = useRef<Channel>(AccountChannel);
  channel.current = accountId
    ? { ...AccountChannel, params: { account_id: accountId } }
    : AccountChannel;

  useChannel(channel.current, handler, enabled && !!accountId);
}

/**
 * SavingsChannel — eventos de actualización de cajas (aportación, capitalización).
 * Suscripción scoped por `user_id` — el BE valida que sea el dueño de la caja.
 *
 * Evento: savings_updated (tras aportación, retiro, o capitalización de intereses).
 * Usado para invalidar portfolio + detail queries vía realtime.
 */
export const SavingsChannel: Channel = { name: 'SavingsChannel' };

export interface SavingsUpdate {
  type: 'savings_updated';
  payload: {
    savings_id: string;
    user_id: string;
    balance_cents: number;
    accumulated_yield_cents: number;
    occurred_at: string;
  };
  timestamp: string;
}

/**
 * InvestmentChannel — eventos de actualización de inversiones (aportación, capitalización).
 * Suscripción scoped por `user_id` — el BE valida que sea el dueño de la inversión.
 *
 * Evento: investment_updated (tras aportación o capitalización de rendimientos).
 * Usado para invalidar portfolio + detail queries vía realtime.
 */
export const InvestmentChannel: Channel = { name: 'InvestmentChannel' };

export interface InvestmentUpdate {
  type: 'investment_updated';
  payload: {
    investment_id: string;
    user_id: string;
    balance_cents: number;
    accumulated_yield_cents: number;
    occurred_at: string;
  };
  timestamp: string;
}

/**
 * Hook para eventos de actualización de cajas (SavingsChannel).
 * Suscripción automática para el current_user del JWT.
 */
export function useSavingsUpdates(handler: (update: SavingsUpdate) => void, enabled = true): void {
  useChannel(SavingsChannel, handler, enabled);
}

/**
 * Hook para eventos de actualización de inversiones (InvestmentChannel).
 * Suscripción automática para el current_user del JWT.
 */
export function useInvestmentUpdates(
  handler: (update: InvestmentUpdate) => void,
  enabled = true
): void {
  useChannel(InvestmentChannel, handler, enabled);
}

/**
 * Hook to manage WebSocket connection lifecycle.
 *
 * Accepts a token getter so reconnects always use the latest token from
 * storage (the HTTP layer may have refreshed it since the initial connect).
 */
export function useWebSocketConnection(getToken: () => string | null): {
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
} {
  const isConnectedRef = useRef(false);
  // Always hold the latest getter — don't capture a stale closure in callbacks
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const connect = useCallback(async () => {
    const token = getTokenRef.current();
    if (token && !isConnectedRef.current) {
      await wsClient.acquire(() => getTokenRef.current());
      isConnectedRef.current = true;
    }
  }, []);

  const disconnect = useCallback(() => {
    wsClient.release();
    isConnectedRef.current = false;
  }, []);

  useEffect(() => {
    const token = getTokenRef.current();
    if (token) {
      void connect().catch(error => {
        logger.warn('WebSocket connection attempt failed', error);
      });
    }

    return () => {
      disconnect();
    };
    // Re-connect when the getter identity changes (new token after login/refresh).
    // The getter itself is stable; callers pass `getStoredToken` (module-level fn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, disconnect]);

  return {
    isConnected: isConnectedRef.current,
    connect,
    disconnect,
  };
}
